/**
 * Single source of truth for every Form Flow constant, id and option/menu list.
 *
 * Platform-agnostic on purpose: this module is written to be consumed by `packages/mobile` as well
 * as `packages/web` (mobile already deep-imports `useFormFlow` from this folder), so nothing here may
 * touch the DOM, MUI, Syncfusion or any web-only component.
 *
 * Label lists are exposed as `get*` factories rather than frozen arrays so `t()` runs at
 * call time and the labels follow a language change. Each package resolves `@i18n` to its
 * own i18next instance, so the same factory returns correctly translated labels on both
 * platforms.
 */
import { t } from "@i18n";
import { formCategories, libraryEntities } from "@Helpers";
import _ from "lodash";
import { toObjectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type {
  FormFlowActionStep,
  FormFlowLevel,
  FormFlowListColumn,
  FormFlowMenuItem,
  FormFlowSection,
  FormFlowStep,
  FormFlowToolBtnState,
  FormFlowUser,
} from "./formFlow.types";

/* ------------------------------------------------------------------ *
 * Step / action / menu identifiers
 * ------------------------------------------------------------------ */

/** Step types a level can contain. Persisted values — do not rename. */
export const FORM_FLOW_STEP_TYPES = {
  FILL: "Fill",
  MODIFY: "Modify",
  VERIFY: "Verify",
} as const;

/** The four level actions. Also used as the "add action" menu ids. */
export const FORM_FLOW_ACTION_IDS = {
  MOVE_FORWARD: "moveForward",
  MOVE_BACKWARD: "moveBackward",
  REJECT_FORM: "rejectForm",
  APPROVE_FORM: "approveForm",
} as const;

/** Actions that are structural and can never be removed from a level. */
export const NON_DELETABLE_ACTION_IDS: string[] = [
  FORM_FLOW_ACTION_IDS.MOVE_FORWARD,
  FORM_FLOW_ACTION_IDS.APPROVE_FORM,
];

/** Every id understood by `handleOptionSelect` in `useFormFlowCreation`. */
export const FORM_FLOW_MENU_ACTIONS = {
  DELETE_LEVEL: "deleteLevel",
  DELETE_STEP: "deleteStep",
  DELETE_ACTION: "deleteAction",
  ADD_LEVEL_AFTER: "addLevelAfter",
  ADD_LEVEL_BEFORE: "addLevelBefore",
  RENAME_LEVEL: "renameLevel",
  DUPLICATE_LEVEL: "duplicateLevel",
  ADD_FILL_STEP: "fill",
  ADD_MODIFY_STEP: "modify",
  ADD_VERIFY_STEP: "verify",
  ADD_MOVE_BACKWARD: FORM_FLOW_ACTION_IDS.MOVE_BACKWARD,
  ADD_REJECT_FORM: FORM_FLOW_ACTION_IDS.REJECT_FORM,
  REMOVE_SECTION: "removeSection",
  REMOVE_USER: "removeUser",
} as const;

/** Toolbar ids for the flows list. */
export const FORM_FLOW_TOOLBAR_IDS = {
  ADD: "custom_add",
  EDIT: "custom_edit",
  DELETE: "custom_delete",
  DUPLICATE: "custom_duplicate",
  ADD_TO_LIBRARY: "custom_addToLibrary",
  ARCHIVE: "custom_archive",
  UNARCHIVE: "custom_unarchive",
} as const;

/** Entries of the split "Create" button on the flows list. */
export const FORM_FLOW_CREATE_MENU_IDS = {
  CREATE: "create",
  CREATE_FROM_LIBRARY: "createFromLibrary",
} as const;

/** Which sub-view of the creation screen is being rendered / saved. */
export const FORM_FLOW_SOURCES = {
  FORM_CREATION: "FORM_CREATION",
  SPACE_SETTINGS: "SPACE_SETTINGS",
} as const;

/* ------------------------------------------------------------------ *
 * Defaults, limits and edit flags
 * ------------------------------------------------------------------ */

/** Default button captions seeded onto a new level. Stored on the flow, then user-editable. */
export const DEFAULT_ACTION_BUTTON_TEXTS = {
  SUBMIT: "Submit",
  REJECT: "Reject",
  APPROVE: "Approve",
  ROLL_BACK: "Revert Back",
} as const;

/** Headings of the two levels every flow starts with. */
export const DEFAULT_LEVEL_HEADINGS = {
  PRE_SUBMISSION: "Pre-Submission",
  POST_SUBMISSION: "Post-submission",
} as const;

/** Prefix used when naming a newly inserted intermediate level. */
export const INTERMEDIATE_LEVEL_HEADING_PREFIX = "Intermediate level-";

/** UI-only keys stripped from a level before it is persisted or handed to a parent. */
export const LEVEL_EDIT_FLAG_FIELDS = [
  "headingEditMode",
  "subBtnEditMode",
  "rejectBtnEditMode",
  "rollBackBtnEditMode",
  "approveBtnEditMode",
] as const;

/** In-place editor limits. */
export const LEVEL_HEADING_MAX_LENGTH = 100;
export const ACTION_BTN_TEXT_MAX_LENGTH = 50;

/** Library popup steps: pick flows, then choose who to assign. */
export const FORM_FLOW_LIBRARY_STEPS = {
  SELECT_FLOWS: 1,
  ASSIGN_USERS: 2,
} as const;

/** How the users of a library / newly created flow are chosen. */
export const USER_SELECTION_MODES = {
  CURRENT_USER: "currentUser",
  ALL_SPACE_USERS: "allSpaceUsers",
} as const;

export const DEFAULT_USER_SELECTION_MODE: string = USER_SELECTION_MODES.CURRENT_USER;

/* ------------------------------------------------------------------ *
 * Option / menu factories
 * ------------------------------------------------------------------ */

/**
 * Radio options for "assign users" prompts. Rendered as radios on web, as a picker on mobile.
 *
 * The labels are deliberately not passed through `t()`: they have no translation keys because the
 * web radios have always rendered them as plain English. Add keys to the locale files first if
 * these should be translated.
 */
export const getUserSelectionModeOptions = (): Array<{ value: string; label: string }> => [
  { value: USER_SELECTION_MODES.CURRENT_USER, label: "Current user" },
  { value: USER_SELECTION_MODES.ALL_SPACE_USERS, label: "All space users" },
];

/**
 * The four level actions with the level keys each one reads/writes. `getActions` filters
 * this list down to the actions a level actually has (by `btnTxtField` presence).
 */
export const getFormFlowActionSteps = (): FormFlowActionStep[] => [
  {
    name: t("Move Forward"),
    id: FORM_FLOW_ACTION_IDS.MOVE_FORWARD,
    btnTxtField: "submitBtnTxt",
    btnTxtEditMode: "subBtnEditMode",
    usersField: "allowedUsersToSubmit",
  },
  {
    name: t("Move Backward"),
    id: FORM_FLOW_ACTION_IDS.MOVE_BACKWARD,
    btnTxtField: "rollBackBtnTxt",
    btnTxtEditMode: "rollBackBtnEditMode",
    usersField: "allowedUsersToRollBack",
  },
  {
    name: t("Reject Form"),
    id: FORM_FLOW_ACTION_IDS.REJECT_FORM,
    btnTxtField: "rejectBtnTxt",
    btnTxtEditMode: "rejectBtnEditMode",
    usersField: "allowedUsersToReject",
  },
  {
    name: t("Approve Form"),
    id: FORM_FLOW_ACTION_IDS.APPROVE_FORM,
    btnTxtField: "approveBtnTxt",
    btnTxtEditMode: "approveBtnEditMode",
    usersField: "allowedUsersToApprove",
  },
];

/** Entries of the split "Create" button on the flows list. */
export const getFormFlowCreateMenuItems = (): FormFlowMenuItem[] => [
  { id: FORM_FLOW_CREATE_MENU_IDS.CREATE, text: t("Create") },
  { id: FORM_FLOW_CREATE_MENU_IDS.CREATE_FROM_LIBRARY, text: t("Create From Library") },
];

/**
 * Toolbar of the active flows list. `prefixIcon` names are shared: web maps them to its icon
 * set, mobile maps them to its own.
 */
export const getFormFlowToolbarOptions = (
  toolBtnState: FormFlowToolBtnState,
): Array<{ name: string; id: string; prefixIcon: string; disabled: boolean }> => [
  { name: t("Create"), id: FORM_FLOW_TOOLBAR_IDS.ADD, prefixIcon: "add", disabled: !toolBtnState.create },
  { name: t("Edit"), id: FORM_FLOW_TOOLBAR_IDS.EDIT, prefixIcon: "editProfile", disabled: !toolBtnState.edit },
  { name: t("Delete"), id: FORM_FLOW_TOOLBAR_IDS.DELETE, prefixIcon: "delete", disabled: !toolBtnState.delete },
  {
    name: t("Duplicate"),
    id: FORM_FLOW_TOOLBAR_IDS.DUPLICATE,
    prefixIcon: "Duplicate",
    disabled: !toolBtnState.duplicate,
  },
  {
    name: t("Add to Library"),
    id: FORM_FLOW_TOOLBAR_IDS.ADD_TO_LIBRARY,
    prefixIcon: "add",
    disabled: !toolBtnState.addToLibrary,
  },
  { name: t("Archive"), id: FORM_FLOW_TOOLBAR_IDS.ARCHIVE, prefixIcon: "archive", disabled: !toolBtnState.archive },
];

/** Toolbar of the archived flows list. */
export const getArchivedToolbarOptions = (archivedToolBtnState: {
  unarchive: boolean;
}): Array<{ name: string; id: string; prefixIcon: string; disabled: boolean }> => [
  {
    name: t("Unarchive"),
    id: FORM_FLOW_TOOLBAR_IDS.UNARCHIVE,
    prefixIcon: "unarchive",
    disabled: !archivedToolBtnState.unarchive,
  },
];

/** Columns of the flows list — metadata only; each platform supplies its own cell renderers. */
export const getFormFlowListColumns = (): FormFlowListColumn[] => [
  { field: "name", id: "name", name: t("Name"), filterType: "Text" },
  { field: "selectedForm", id: "selectedForm", name: t("Form Template"), filterType: "Text" },
  { field: "space.spaceName", id: "space", name: t("Space Name"), filterType: "Text" },
  { field: "createdBy", id: "createdBy", name: t("Created by"), filterType: "Text" },
  { field: "createdDate", id: "createdDate", name: t("Created Date"), filterType: "Date" },
];

/**
 * Level "three dots" menu. The first and last levels are structural: they cannot be deleted,
 * nothing may be inserted after the last one, and nothing before the first one.
 *
 * NOTE: "Delete" and "Duplicate" are intentionally left untranslated here to match the
 * pre-extraction behaviour of the web editor; translate them in a follow-up if wanted.
 */
export const getLevelMenuItems = ({
  levelIndex,
  totalLevels,
}: {
  levelIndex: number;
  totalLevels: number;
}): FormFlowMenuItem[] => {
  const isLast = levelIndex === totalLevels - 1;
  const isFirst = levelIndex === 0;
  return [
    ...(!isLast ? [{ text: t("Add level after"), id: FORM_FLOW_MENU_ACTIONS.ADD_LEVEL_AFTER }] : []),
    ...(!isFirst ? [{ text: t("Add level before"), id: FORM_FLOW_MENU_ACTIONS.ADD_LEVEL_BEFORE }] : []),
    { text: t("Rename"), id: FORM_FLOW_MENU_ACTIONS.RENAME_LEVEL },
    ...(!isLast && !isFirst ? [{ text: "Delete", id: FORM_FLOW_MENU_ACTIONS.DELETE_LEVEL }] : []),
    ...(!isLast ? [{ text: "Duplicate", id: FORM_FLOW_MENU_ACTIONS.DUPLICATE_LEVEL }] : []),
  ];
};

/** Step "three dots" menu. (Untranslated "Delete" preserved from the original editor.) */
export const getStepMenuItems = (): FormFlowMenuItem[] => [{ text: "Delete", id: FORM_FLOW_MENU_ACTIONS.DELETE_STEP }];

/** Action "three dots" menu. (Untranslated "Delete" preserved from the original editor.) */
export const getActionMenuItems = (): FormFlowMenuItem[] => [
  { text: "Delete", id: FORM_FLOW_MENU_ACTIONS.DELETE_ACTION },
];

/** "Add step" menu. Verify only makes sense on the final level. */
export const getAddStepMenuItems = ({
  levelIndex,
  totalLevels,
}: {
  levelIndex: number;
  totalLevels: number;
}): FormFlowMenuItem[] => [
  { text: t("Fill"), id: FORM_FLOW_MENU_ACTIONS.ADD_FILL_STEP },
  { text: t("Modify"), id: FORM_FLOW_MENU_ACTIONS.ADD_MODIFY_STEP },
  ...(levelIndex === totalLevels - 1 ? [{ text: t("Verify"), id: FORM_FLOW_MENU_ACTIONS.ADD_VERIFY_STEP }] : []),
];

/**
 * "Add action" menu. Both entries are one-shot: an action already present on the level is
 * disabled, and "Move Backward" is never available on the first level (there is nowhere to
 * go back to). The `disabled` flags are computed here so both platforms agree.
 */
export const getAddActionMenuItems = ({
  level,
  levelIndex,
}: {
  level: FormFlowLevel;
  levelIndex: number;
}): FormFlowMenuItem[] => [
  {
    text: t("Move Backward"),
    id: FORM_FLOW_MENU_ACTIONS.ADD_MOVE_BACKWARD,
    disabled: levelIndex === 0 || Object.hasOwn(level, "allowedUsersToRollBack"),
  },
  {
    text: t("Reject Form"),
    id: FORM_FLOW_MENU_ACTIONS.ADD_REJECT_FORM,
    disabled: Object.hasOwn(level, "allowedUsersToReject"),
  },
];

/* ------------------------------------------------------------------ *
 * Factories: fresh flow / level / step objects
 * ------------------------------------------------------------------ */

/**
 * The two default levels of a brand-new flow: `[preSubmission, postSubmission]`. A factory (not a
 * shared constant) so callers can safely mutate the result — `setInitialData` fills in
 * `dataSource` and the user lists.
 */
export const getInitialFormFlowLevels = (): [FormFlowLevel, FormFlowLevel] => [
  {
    heading: DEFAULT_LEVEL_HEADINGS.PRE_SUBMISSION,
    headingEditMode: false,
    dataSource: [],
    submitBtnTxt: DEFAULT_ACTION_BUTTON_TEXTS.SUBMIT,
    rejectBtnTxt: DEFAULT_ACTION_BUTTON_TEXTS.REJECT,
    subBtnEditMode: false,
    rejectBtnEditMode: false,
    actionStatus: false,
    actionDetails: {},
    allowedUsersToSubmit: [],
    allowedUsersToReject: [],
  },
  {
    heading: DEFAULT_LEVEL_HEADINGS.POST_SUBMISSION,
    headingEditMode: false,
    dataSource: [],
    approveBtnTxt: DEFAULT_ACTION_BUTTON_TEXTS.APPROVE,
    rejectBtnTxt: DEFAULT_ACTION_BUTTON_TEXTS.REJECT,
    rollBackBtnTxt: DEFAULT_ACTION_BUTTON_TEXTS.ROLL_BACK,
    approveBtnEditMode: false,
    allowDynamicUserConfig: false,
    rejectBtnEditMode: false,
    rollBackBtnEditMode: false,
    actionStatus: false,
    actionDetails: {},
    allowedUsersToApprove: [],
    allowedUsersToReject: [],
    allowedUsersToRollBack: [],
  },
];

/** A newly inserted intermediate level: submit + reject only, no steps yet. */
export const createIntermediateLevel = (totalLevels: number): FormFlowLevel => ({
  heading: `${INTERMEDIATE_LEVEL_HEADING_PREFIX}${Math.max(totalLevels, 1)}`,
  headingEditMode: false,
  dataSource: [],
  allowedUsersToSubmit: [],
  allowedUsersToReject: [],
  submitBtnTxt: DEFAULT_ACTION_BUTTON_TEXTS.SUBMIT,
  rejectBtnTxt: DEFAULT_ACTION_BUTTON_TEXTS.REJECT,
  subBtnEditMode: false,
  rejectBtnEditMode: false,
  rollBackBtnEditMode: false,
});

/** An empty step of the given type. */
export const createFlowStep = (type: FormFlowStep["type"]): FormFlowStep => ({ type, sections: [], users: [] });

/** Reduce a template section to the identity fields the flow stores. */
export const toFlowSection = (section: FormFlowSection): FormFlowSection => ({
  _id: section._id,
  sectionCode: section.sectionCode,
  sectionHeading: section.sectionHeading,
});

/**
 * `{ id, name }` shape the flow stores for a user, built from `fullName`. Used when seeding the
 * default levels of a new flow.
 */
export const toFlowUserFromFullName = (user: { id: ObjectIdString; fullName?: string }): FormFlowUser => ({
  id: user.id,
  name: `${user.fullName}`,
});

/**
 * Same shape, built from first + last name. Used by "Create from Library".
 *
 * The two helpers differ only in which name field they read. They are kept separate rather than
 * unified so each screen keeps sending the exact payload it always has; unify them once it is
 * confirmed that `fullName` never differs from `firstName + lastName`.
 */
export const toFlowUserFromNameParts = (user: {
  id: ObjectIdString;
  firstName?: string;
  lastName?: string;
}): FormFlowUser => ({
  id: user.id,
  name: `${user.firstName} ${user.lastName}`,
});

/** Strip the UI-only in-place-edit flags. Returns a clone; the input is never mutated. */
export const stripLevelEditFlags = (level: FormFlowLevel): FormFlowLevel => {
  const cleaned = _.cloneDeep(level);
  LEVEL_EDIT_FLAG_FIELDS.forEach((field) => {
    delete cleaned[field];
  });
  return cleaned;
};

/** Strip edit flags from every level of a flow, ready to send to the server. */
export const toPersistableFormFlow = (levels: FormFlowLevel[]): FormFlowLevel[] => levels.map(stripLevelEditFlags);

/* ------------------------------------------------------------------ *
 * Derived state helpers
 * ------------------------------------------------------------------ */

/** No rows selected: only "Create" is available. */
export const NO_SELECTION_TOOL_BTN_STATE: FormFlowToolBtnState = {
  create: true,
  edit: false,
  delete: false,
  duplicate: false,
  addToLibrary: false,
  archive: false,
};

/**
 * Which list actions the current selection allows. Edit and Duplicate are single-row only;
 * Delete, Add-to-Library and Archive work on any non-empty selection.
 */
export const getToolBtnStateForSelection = (selectedCount: number): FormFlowToolBtnState => {
  if (selectedCount === 1) {
    return { create: true, delete: true, edit: true, duplicate: true, addToLibrary: true, archive: true };
  }
  if (selectedCount > 1) {
    return { create: true, delete: true, edit: false, duplicate: false, addToLibrary: true, archive: true };
  }
  return { ...NO_SELECTION_TOOL_BTN_STATE };
};

/** Add/edit dialog defaults. `formCategory` falls back to Inspection when none is supplied. */
export const getInitialFormFlowFormValues = ({
  formCategory,
  projectId,
}: {
  formCategory?: string;
  projectId?: string;
}) => ({
  name: "",
  selectedTemplate: null,
  formCategory: formCategory?.toUpperCase() || formCategories.inspection,
  projectId,
});

/** Grid/list query for the flows of a project, split by archived state. */
export const buildFormFlowListQuery = ({
  projectId,
  formCategory,
  isArchived,
}: {
  projectId?: string;
  formCategory?: string;
  isArchived: boolean;
}) => ({ projectId, formCategory, isArchived });

/** Query that lists the FORM_FLOW library entries of one form category. */
export const buildFormFlowLibraryQuery = (resourceType: string) => ({
  entity: libraryEntities.FORM_FLOW,
  where: [
    {
      isComplex: false,
      field: "data.formCategory",
      operator: "equal",
      ignoreCase: false,
      value: resourceType,
    },
  ],
});

/** Space-scoped query for the add-user picker. */
/**
 * The users-list query the flow editor hands to the add-user modal: every user of the flow's space.
 *
 * Returns exactly what `getUsersListRequest.value` models. The nested `params: { … }` duplicate this
 * used to carry was never read — `getUsersList` reads the fields off the top level — so it only
 * survived because the query was untyped.
 */
export const buildAddUserQuery = (spaceId: string): { spaceId: ObjectIdString; selectionScope: "space" } => ({
  spaceId: toObjectIdString(spaceId),
  selectionScope: "space",
});

/** Ids of the sections referenced by a step. */
export const getStepSectionIds = (step: FormFlowStep): string[] => step.sections.map((section) => section._id);

/**
 * Every section id used by a Fill step and by a Verify step, across all levels. A flow is only
 * saveable once both cover the whole template — see `isFormFlowSaveable`.
 */
export const collectUsedSectionIds = (levels: FormFlowLevel[]) => {
  const fill: string[] = [];
  const verify: string[] = [];
  levels.forEach((level) => {
    level.dataSource.forEach((step) => {
      const sectionIds = getStepSectionIds(step);
      if (step.type === FORM_FLOW_STEP_TYPES.VERIFY) {
        verify.push(...sectionIds);
      }
      if (step.type === FORM_FLOW_STEP_TYPES.FILL) {
        fill.push(...sectionIds);
      }
    });
  });
  return { fill, verify };
};

/**
 * A flow is saveable when every template section is covered by both a Fill and a Verify step,
 * every step has at least one user, and every action present on a level has at least one user.
 */
export const isFormFlowSaveable = ({
  levels,
  templateSectionCount,
  sectionsUsedForFill,
  sectionsUsedForVerify,
}: {
  levels: FormFlowLevel[];
  /** `undefined` while the template is still loading — see the coverage guard below. */
  templateSectionCount: number | undefined;
  sectionsUsedForFill: string[];
  sectionsUsedForVerify: string[];
}): boolean => {
  // Coverage is only enforced once the template has loaded. Before that the two comparisons were
  // `n < undefined`, i.e. always false, so only the per-level user checks decided — the behaviour
  // the editor has always had, written as the guard it actually is.
  if (templateSectionCount !== undefined) {
    if (new Set(sectionsUsedForFill).size < templateSectionCount) return false;
    if (new Set(sectionsUsedForVerify).size < templateSectionCount) return false;
  }

  return levels.every((level) => {
    if (level.dataSource.length && level.dataSource.some((step) => step.users.length === 0)) return false;
    const actionUserLists: Array<FormFlowUser[] | undefined> = [
      level.allowedUsersToApprove,
      level.allowedUsersToReject,
      level.allowedUsersToRollBack,
      level.allowedUsersToSubmit,
    ];
    return actionUserLists.every((users) => !users || users.length !== 0);
  });
};

/**
 * Section ids already "spoken for" from the point of view of one step, i.e. the ids the
 * add-sections picker must hide. Fill and Verify steps share their coverage across the whole
 * flow (a section may be filled/verified once); a Modify step only excludes its own sections.
 */
export const getSectionsInUseForStep = ({
  step,
  sectionsUsedForFill,
  sectionsUsedForVerify,
}: {
  step: FormFlowStep;
  sectionsUsedForFill: string[];
  sectionsUsedForVerify: string[];
}): string[] => {
  if (step.type === FORM_FLOW_STEP_TYPES.FILL) return [...sectionsUsedForFill];
  if (step.type === FORM_FLOW_STEP_TYPES.VERIFY) return [...sectionsUsedForVerify];
  // Only Modify is left — `type` is `z.enum(["Fill", "Modify", "Verify"])` on the wire, so the
  // Modify test and the `return []` behind it could never decide anything.
  return step.sections.map((section) => section._id);
};

/**
 * Sections offered by the add-sections picker: everything not already in use, except for a
 * Modify step, which may only modify sections that some Fill step covers.
 *
 * The Modify branch also excludes `currentSections` here. The pre-extraction editor compared
 * whole section objects against a list of ids in that branch (`includes(section)` instead of
 * `includes(section._id)`), so the exclusion silently never matched and an already-added
 * section could be added to the same Modify step twice.
 */
export const getSelectableSections = <T extends { _id: string }>({
  template,
  currentSections,
  stepType,
  sectionsUsedForFill,
}: {
  template: T[];
  currentSections: string[];
  stepType?: string;
  sectionsUsedForFill: string[];
}): T[] => {
  if (stepType === FORM_FLOW_STEP_TYPES.MODIFY) {
    return template
      .filter((section) => sectionsUsedForFill.includes(section._id))
      .filter((section) => !currentSections.includes(section._id));
  }
  return template.filter((section) => !currentSections.includes(section._id));
};
